import { UiBreadcrumbItem } from '../../atoms/breadcrumb/breadcrumb.component';
import { UiTitleBarMode } from '../../organisms/title-bar/title-bar.types';
import type { UiAppLayoutVariant } from '../app-layout/app-layout.component';

/**
 * Marco de página (*page chrome*): todo lo que rodea al contenido de una vista
 * y no forma parte de ella —header, sidebar, menubar, barra de título,
 * breadcrumb, acciones y pestañas—.
 *
 * Una página **no** instancia `ui-app-layout`: declara su marco y el App Shell
 * de la aplicación lo pinta en la única instancia del layout.
 *
 * Se declara en la configuración de ruta bajo la llave `chrome`:
 *
 * ```ts
 * {
 *   path: 'companies/companies',
 *   loadComponent: ...,
 *   data: {
 *     chrome: {
 *       title: 'Empresas',
 *       titleBarMode: 'data-table',
 *       moduleId: 'companies',
 *       activeMenubarId: 'companies',
 *     } satisfies UiPageChromeConfig,
 *   },
 * }
 * ```
 */
export interface UiPageChrome {
  /** Variante del layout que envuelve la página. */
  variant: UiAppLayoutVariant;

  /** Título de la barra de título. */
  title: string;

  /**
   * `aria-label` del `<main>`. Si se omite, se usa el título de la página.
   */
  mainLabel: string;

  /** Modo de la barra de título: define qué acepta su zona derecha. */
  titleBarMode: UiTitleBarMode;

  /** `false` en vistas full-screen (dashboards, lienzos). */
  showTitleBar: boolean;

  /** `false` en vistas sin navegación de sección. */
  showMenubar: boolean;

  /**
   * `false` oculta el slot de filtro de empresas en el App Header
   * (`[data-ui-app-header-end-start]`).
   */
  showCompanyFilter: boolean;

  /** Ruta de navegación. El último ítem corresponde a la vista actual. */
  breadcrumbItems: readonly UiBreadcrumbItem[];

  /** Ítem inicial del breadcrumb, normalmente con icono de inicio. */
  breadcrumbHome: UiBreadcrumbItem | null;

  /**
   * Módulo al que pertenece la página. El shell lo usa para marcar el ítem
   * activo del sidebar y para resolver los ítems del menubar.
   */
  moduleId: string | null;

  /** Ítem del menubar que queda marcado como activo. */
  activeMenubarId: string | null;
}

/**
 * Marco parcial. Es la forma en que las rutas y las páginas declaran su marco:
 * solo lo que difiere de {@link UI_PAGE_CHROME_DEFAULTS}.
 */
export type UiPageChromeConfig = Partial<UiPageChrome>;

/**
 * Valores base del marco. El shell parte de aquí en cada navegación, de modo
 * que ninguna propiedad de la vista anterior sobrevive a la siguiente.
 */
export const UI_PAGE_CHROME_DEFAULTS: UiPageChrome = {
  variant: 'sidebar',
  title: '',
  mainLabel: '',
  titleBarMode: 'data-table',
  showTitleBar: true,
  showMenubar: true,
  showCompanyFilter: false,
  breadcrumbItems: [],
  breadcrumbHome: null,
  moduleId: null,
  activeMenubarId: null,
};
